import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Grid from '@mui/material/Grid2'
import Footer from 'Components/General/Footer'
import NavigationMenu from 'Components/General/NavigationMenu'
import { useMenu } from 'Components/General/NavigationMenu/menu'
import Toolbar, { ToolbarProvider } from 'Components/General/Toolbar'
import en from 'date-fns/locale/en-GB'
import fi from 'date-fns/locale/fi'
import sv from 'date-fns/locale/sv'
import { useLogin, useNotifications, useUser } from 'hooks'
import { useEffect } from 'react'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, Outlet } from 'react-router'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { removeNonCaleoStorage } from 'utils/utils'
import backgroundImage from 'assets/images/background.png'
import ChatNotification from 'Components/General/ChatContext/ChatNotification'
import { HelpButtonProvider } from 'Components/General/HelpButtonProvider'
import ChatProvider from 'Components/General/ChatContext'
import MaintenanceAlert from 'Components/General/Admin/AdminMaintenance/MaintenanceAlert'

/**
 * Root layout component. Renders the chrome (navigation, toolbar, footer) around page content.
 * Handles locale registration, background switching, and auth error recovery.
 */
function Layout() {
  const { loggedIn, setLoginLoading, setLoggedIn, setLoggingIn, error } = useLogin()
  const { ready, user } = useUser()
  const { drawerWidth } = useMenu()
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'))
  const { setRefresh } = useNotifications()
  const location = useLocation()
  const navigate = useNavigate()

  const { i18n } = useTranslation()
  registerLocale('en', en)
  registerLocale('fi', fi)
  registerLocale('sv', sv)
  setDefaultLocale(i18n.language)

  useEffect(() => {
    setRefresh()

    if (
      location.pathname.includes('/login') ||
      location.pathname.includes('/activate') ||
      location.pathname.includes('/confirm') ||
      location.pathname.includes('/reset-password') ||
      location.pathname.includes('/change-password') ||
      location.pathname.includes('/forgot-password') ||
      location.pathname.includes('/terms') ||
      location.pathname.includes('/privacy-policy') ||
      location.pathname.includes('/logout')
    ) {
      document.body.style.background = `url(${backgroundImage})`
    } else {
      document.body.style.background = 'none'
    }

    return () => {
      document.body.style.background = 'none'
    }
  }, [location.pathname, setRefresh])

  useEffect(() => {
    if (error && error.includes('azureLogin')) {
      removeNonCaleoStorage()
      setLoginLoading(false)
      setLoggedIn(false)
      setLoggingIn(false)
      //navigate('/login')
    }
  }, [error, navigate, setLoggedIn, setLoggingIn, setLoginLoading])

  useEffect(() => {
    ;(async () => {
      const locales = i18n.languages
      if (user && user.language && user.language !== i18n.language && locales && locales.includes(user.language)) {
        await i18n.changeLanguage(user.language)
      }
    })()
  }, [i18n, user])

  if (!ready && loggedIn && location.pathname !== '/activate') {
    return (
      <Grid>
        <LoadingIndicator />
      </Grid>
    )
  }

  let hideToolbarMenu = true

  if (loggedIn) {
    hideToolbarMenu = false
  }

  const toolbarPagestyles: { margin: number | string; marginLeft?: number | string; width?: string } = { margin: 0 }
  if (hideToolbarMenu) {
    toolbarPagestyles.margin = 0
    toolbarPagestyles.width = '100%'
  } else {
    if (mobileView) {
      toolbarPagestyles.margin = 0
      toolbarPagestyles.width = '100%'
    } else {
      toolbarPagestyles.marginLeft = drawerWidth
      toolbarPagestyles.width = `calc(100% - ${drawerWidth}px)`
    }
  }

  return (
    <ChatProvider>
      <HelpButtonProvider>
        <ToolbarProvider>
          <ChatNotification />
          <NavigationMenu />
          <Grid sx={toolbarPagestyles}>
            <MaintenanceAlert />
            <Toolbar />
            <Grid
              sx={{
                marginBottom: '5px',
                marginTop: '15px',
                minHeight: 'calc(100vh - 69px - 74px)',
              }}
            >
              <Outlet />
            </Grid>
            <Footer />
          </Grid>
        </ToolbarProvider>
      </HelpButtonProvider>
    </ChatProvider>
  )
}

export default Layout
